/**
 * ZIP import/export for per-player data backup.
 * Uses fflate for browser-only zip creation and parsing.
 */

import { strToU8, strFromU8, zipSync, unzipSync } from "fflate";
import type { Team, TrainerProfile } from "../engine/types";
import type { Theme } from "../config/themes";
import { getCookie, setCookie } from "../ui/utils/cookies";
import { COOKIE_KEYS, DB_STORES } from "../config/constants";
import { dbGet } from "./db";

export type BackupData = {
  version: string;
  teams: Team[];
  trainer: TrainerProfile | null;
  themeId: string | null;
  customThemes: Theme[];
  lastTeamId: string | null;
  exportedAt: string;
};

export function collectReferencedSpriteUrls(
  teams: Team[],
  trainer: TrainerProfile | null,
): string[] {
  const urls: string[] = [];
  if (trainer) {
    if (trainer.backSprite) urls.push(trainer.backSprite);
    if (trainer.frontSprite) urls.push(trainer.frontSprite);
    if (trainer.vsSprite) urls.push(trainer.vsSprite);
  }
  for (const team of teams) {
    for (const member of team.members) {
      if (member.customSprite?.front) urls.push(member.customSprite.front);
      if (member.customSprite?.back) urls.push(member.customSprite.back);
    }
  }
  return [...new Set(urls)];
}

export async function exportZip(
  teams: Team[],
  trainer: TrainerProfile | null,
  themeId: string | null,
  customThemes: Theme[],
  lastTeamId: string | null,
): Promise<Blob> {
  const backupData: BackupData = {
    version: "1.0",
    teams,
    trainer,
    themeId,
    customThemes,
    lastTeamId,
    exportedAt: new Date().toISOString(),
  };
  const files: Record<string, Uint8Array> = {
    "backup.json": strToU8(JSON.stringify(backupData, null, 2)),
  };

  // Include referenced custom sprites from IndexedDB cache
  const referencedUrls = collectReferencedSpriteUrls(teams, trainer);
  for (const url of referencedUrls) {
    if (url.startsWith("data:")) {
      // Convert data URL back to bytes and store with a stable filename
      const blob = dataUrlToBlob(url);
      if (blob) {
        const filename = spriteUrlToFilename(url);
        files[`sprites/${filename}`] = new Uint8Array(await blob.arrayBuffer());
      }
    } else {
      const cached = await dbGet<Blob>(DB_STORES.SPRITES, url);
      if (cached) {
        const filename = spriteUrlToFilename(url);
        files[`sprites/${filename}`] = new Uint8Array(await cached.arrayBuffer());
      }
    }
  }

  const zipped = zipSync(files);
  return new Blob([zipped], { type: "application/zip" });
}

function spriteUrlToFilename(url: string): string {
  // Strip protocol and leading slashes; replace path separators with underscores
  return url.replace(/^data:image\/[^;]+;base64,/, "data_")
    .replace(/^https?:\/\//, "")
    .replace(/^\//, "")
    .replace(/\//g, "_");
}

function dataUrlToBlob(dataUrl: string): Blob | null {
  try {
    const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!match) return null;
    const [, mime, base64] = match;
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return new Blob([bytes], { type: mime });
  } catch {
    return null;
  }
}

export async function importZip(file: File): Promise<{
  data: BackupData;
  sprites: { url: string; blob: Blob }[];
}> {
  const buf = new Uint8Array(await file.arrayBuffer());
  const unzipped = unzipSync(buf);
  const backupFile = unzipped["backup.json"];
  if (!backupFile) throw new Error("Invalid backup file: missing backup.json");
  const data = JSON.parse(strFromU8(backupFile)) as BackupData;
  const sprites: { url: string; blob: Blob }[] = [];
  for (const [path, content] of Object.entries(unzipped)) {
    if (path.startsWith("sprites/")) {
      const filename = path.slice("sprites/".length);
      sprites.push({ url: filename, blob: new Blob([content]) });
    }
  }
  return { data, sprites };
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadJson(data: unknown, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  downloadBlob(blob, filename);
}

export function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.addEventListener("change", () => resolve(input.files?.[0] ?? null));
    input.addEventListener("cancel", () => resolve(null));
    input.click();
  });
}

export function readFileAsText(file: File): Promise<string> {
  return file.text();
}

export function restoreCookies(data: BackupData): void {
  if (data.themeId) setCookie(COOKIE_KEYS.THEME, data.themeId);
  if (data.trainer) setCookie(COOKIE_KEYS.TRAINER, data.trainer);
  if (data.lastTeamId) setCookie(COOKIE_KEYS.LAST_TEAM, data.lastTeamId);
}

export function collectCookieData(): {
  themeId: string | null;
  trainer: TrainerProfile | null;
  lastTeamId: string | null;
} {
  return {
    themeId: getCookie<string | null>(COOKIE_KEYS.THEME, null),
    trainer: getCookie<TrainerProfile | null>(COOKIE_KEYS.TRAINER, null),
    lastTeamId: getCookie<string | null>(COOKIE_KEYS.LAST_TEAM, null),
  };
}